import axios from '~/utils/axiosInstance';
import type {
  BuyerProfile,
  BuyerProfileAnswerEntry,
  MatchResult,
  OnboardingState,
  QuestionConfig,
} from '~/features/buyerProfile/types';

/** Fetch the question configuration (the 17 questions as data). */
export const getBuyerProfileQuestions = async function (): Promise<QuestionConfig> {
  const response = await axios.get('/buyer-profile/questions');
  return response.data;
};

/** Fetch the buyer profile for the logged-in user's company (admins may pass companyId). */
export const getBuyerProfile = async function (
  companyId?: string
): Promise<BuyerProfile> {
  const response = await axios.get('/buyer-profile', {
    params: companyId ? { companyId } : undefined,
  });
  return response.data;
};

/** Upsert a batch of answers. Partial batches are allowed — untouched answers are preserved. */
export const saveBuyerProfileAnswers = async function (
  answers: BuyerProfileAnswerEntry[],
  companyId?: string
): Promise<BuyerProfile> {
  const response = await axios.put('/buyer-profile/answers', {
    answers,
    ...(companyId ? { companyId } : {}),
  });
  return response.data;
};

/**
 * Update onboarding-wizard progress (Phase 2): persist the resume cursor and/or
 * flip status to 'onboarded' on completion. Both fields are optional.
 */
export const updateBuyerProfileOnboarding = async function (
  update: { onboardingState?: OnboardingState; status?: 'draft' | 'onboarded' },
  companyId?: string
): Promise<BuyerProfile> {
  const response = await axios.put('/buyer-profile/onboarding', {
    ...update,
    ...(companyId ? { companyId } : {}),
  });
  return response.data;
};

/**
 * Score a deal against an investor company's buyer profile (Phase 3,
 * admin-only). Pass exactly one of projectId / portfolioId.
 */
export const scoreBuyerProfileMatch = async function (params: {
  investorCompanyId: string;
  projectId?: string;
  portfolioId?: string;
}): Promise<MatchResult> {
  const response = await axios.post('/buyer-profile/match', params);
  return response.data;
};

// ---------------------------------------------------------------------------
// Bulk match scoring (Phase 3) — admin scores every investor against one deal
// asynchronously, persists rows, then loads a paginated list.
// ---------------------------------------------------------------------------

export interface StartBulkMatchResponse {
  apiEvent: { id: string };
}

/** Enqueue a bulk-scoring run for one deal. Returns the ApiEvent hashid to poll. */
export const startBulkMatchScoring = async function (params: {
  projectId?: string;
  portfolioId?: string;
}): Promise<StartBulkMatchResponse> {
  const response = await axios.post('/buyer-profile/match/bulk', params);
  return response.data;
};

export interface BulkMatchRunStatus {
  status: 'PENDING' | 'SUCCESS' | 'ERROR';
  total?: number;
  processed?: number;
  errorMessage?: string;
  projectId?: string | null;
  portfolioId?: string | null;
}

/** Poll a bulk-scoring run. Used to drive the progress UI. */
export const pollBulkMatchScoring = async function (
  apiEventId: string
): Promise<BulkMatchRunStatus> {
  const response = await axios.get(`/buyer-profile/match/runs/${apiEventId}`);
  return response.data;
};

export interface BulkMatchScoreRow {
  id: string;
  apiEventId: string;
  investorCompany: { id: string; name: string };
  score: number;
  exclusionTotal: number;
  exclusionBand: 'green' | 'yellow' | 'red';
  killed: boolean;
  scoredAt: string;
  result: MatchResult;
}

export interface ListBulkMatchScoresResponse {
  rows: BulkMatchScoreRow[];
  total: number;
  page: number;
  pageSize: number;
  apiEventId: string | null;
}

/** Paginated list of stored scores for one deal (latest run by default). */
export const listBulkMatchScores = async function (params: {
  projectId?: string;
  portfolioId?: string;
  apiEventId?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListBulkMatchScoresResponse> {
  const response = await axios.get('/buyer-profile/match/scores', { params });
  return response.data;
};
